import React, { useState } from 'react'
import { object, string } from 'yup';
import { useFormik } from 'formik';

// Constants
import { PASSWORD_LENGTH_LIMITS } from '../../constants/common';

// Utils
import { PASSWORD_RULES_HINT, getPasswordStrengthError } from '../../utils/passwordRules';

// Components
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import InputControl from '../InputControl/InputControl';

type ProfilePasswordFormProps = {
  changePassword: ({ currentPassword, newPassword }: { currentPassword: string, newPassword: string }) => Promise<boolean>;
};

const ProfilePasswordForm: React.FC<ProfilePasswordFormProps> = ({ changePassword }) => {
  const defaultVisibilityPwd = {
    currentPassword: false,
    newPassword: false,
  }

  const [visibilityPwd, setVisibilityPwd] = useState(defaultVisibilityPwd);

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
    },
    validationSchema: object({
      currentPassword: string()
        .min(PASSWORD_LENGTH_LIMITS.MIN, `Min password length is ${PASSWORD_LENGTH_LIMITS.MIN} characters`)
        .max(PASSWORD_LENGTH_LIMITS.MAX, `Max password length is ${PASSWORD_LENGTH_LIMITS.MAX} characters`)
        .required('Required'),
      newPassword: string()
        .required('Required')
        .test('password-strength', function (value) {
          const error = getPasswordStrengthError(value || '');

          return error ? this.createError({ message: error }) : true;
        }),
    }),
    onSubmit: async (values) => {
      const { currentPassword, newPassword } = values;

      await changePassword({
        currentPassword,
        newPassword
      });
    },
  });

  const isDisabledBtn = Boolean((formik.touched.currentPassword && formik.errors.currentPassword) || (formik.touched.newPassword && formik.errors.newPassword));

  const resetNewData = () => {
    formik.resetForm()
  }

  const changePwdVisibility = (key: string) => setVisibilityPwd((oldData) => ({ ...oldData, [key]: !oldData[key] }));

  return (
    <form className='c-profile-email-form' onSubmit={formik.handleSubmit}>
      <div className="pb-5.5 flex flex-col gap-5.5 sm:flex-row">
        <div className="flex-auto">
          <InputControl
            label='Current password'
            type="password"
            name='currentPassword'
            placeholder="Enter your current password"
            changePwdVisibility={() => changePwdVisibility('currentPassword')}
            showPwd={visibilityPwd.currentPassword}
            value={formik.values.currentPassword}
            customOnChange={formik.handleChange}
            errorText={formik.errors.currentPassword}
            showError={formik.touched.currentPassword && formik.errors.currentPassword}
            onBlur={formik.handleBlur}
          />
        </div>
        <div className="flex-auto">
          <InputControl
            label='New password'
            type="password"
            name='newPassword'
            placeholder="Enter your code"
            changePwdVisibility={() => changePwdVisibility('newPassword')}
            showPwd={visibilityPwd.newPassword}
            value={formik.values.newPassword}
            customOnChange={formik.handleChange}
            errorText={formik.errors.newPassword}
            showError={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
            suggestion={PASSWORD_RULES_HINT}
            onBlur={formik.handleBlur}
          />
        </div>
      </div>
      <div className="flex justify-end gap-4.5">
        <InteractiveButton
          text='Cancel'
          customType='bg-zinc-100 text-black border border-stroke border-zinc-300 hover:bg-zinc-200 hover:border-zinc-300 disabled:bg-zinc-400 disabled:border-zinc-600'
          className="text-black"
          onClick={resetNewData}
          disabled={formik.isSubmitting}
        />
        <InteractiveButton
          text='Save'
          disabled={isDisabledBtn}
          submit
          isLoading={formik.isSubmitting}
        />
      </div>
    </form>
  )
}

export default ProfilePasswordForm;
